import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  FormControlLabel,
  MenuItem,
  Paper,
  Stack,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material'
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord'
import FilterAltOffOutlinedIcon from '@mui/icons-material/FilterAltOffOutlined'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline'
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined'
import SendOutlinedIcon from '@mui/icons-material/SendOutlined'
import VideoLibraryOutlinedIcon from '@mui/icons-material/VideoLibraryOutlined'
import { Suspense, lazy, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Navigate } from 'react-router'
import { useNavigate } from 'react-router'
import { toast } from 'react-toastify'
import { ReceiptPrintDialogPaper } from 'src/clinic/components/ClinicManageBookingsPage/receiptPrinting/styledComponents'
import SortingHandler from 'src/common/tableRelated/SortingHandler'
import BaseUITabsComponent from 'src/common/tabs/BaseUITabs'
import MentorPastEventsListing from 'src/mentor/components/MentorEventsPage/components/bookingListing/MentorPastEventsListing'
import { isCommonResponseSuccessfulV2 } from 'src/mentor/helpers/utilityFunctions'
import { getUserRoles } from 'src/mentor/layout/utils'
import SupportBookedEventRescheduleDrawer from 'src/supportPortal/components/bookings/SupportBookedEventRescheduleDrawer'
import SupportBookedEventReassignDrawer from 'src/supportPortal/components/bookings/SupportBookedEventReassignDrawer'
import SupportBookedEventDetailDrawer from 'src/supportPortal/components/bookings/SupportBookedEventDetailDrawer'
import SupportBookedEventManageMenu from 'src/supportPortal/components/bookings/SupportBookedEventManageMenu'
import SupportMarkAsPaidDialog from 'src/supportPortal/components/bookings/SupportMarkAsPaidDialog'
import SupportHeader from 'src/supportPortal/layout/SupportHeader'
import SupportContactActions from 'src/supportPortal/components/contact/SupportContactActions'
import EngagementSettingsDialog from 'src/supportPortal/components/EngagementSettingsDialog'
import { canManageEngagement } from 'src/supportPortal/services/patientEngagementService'
import { putSPDataWithParams } from 'src/supportPortal/services/services'
import { fetchClinicBrandsWithLocations } from 'src/supportPortal/services/clinicWorkforceService'
import { searchSupportReassignPractitioners } from 'src/supportPortal/services/supportBookedEventActionsService'
import {
  canActOnSupportBookedEvent,
  canMarkSupportBookedEventPaid,
  canViewSupportBookings,
  canViewSupportVcAuditBookings,
  getAllowedSupportBookedEventTypeOptions,
  hasAnySupportRole,
  SUPPORT_BOOKED_EVENT_TYPE_OPTIONS_CLINIC_SUCCESS,
  canManageSupportBookedEventInvoices,
  canManageSupportRecordingPreferences,
} from 'src/supportPortal/access/accessMatrix'
import { BOOKING_PAYMENT_STATUSES } from 'src/consumer/constants'
import { getSPDataWithoutParams, postSPDataWithoutParams } from 'src/supportPortal/services/services'

const ReceiptPrintingComponent = lazy(() =>
  import('src/clinic/components/ClinicManageBookingsPage/receiptPrinting/ReceiptPrintingComponent')
)

const SORT_OPTIONS = [
  { label: 'Start Time', value: 'startTime' },
  { label: 'Booking Time', value: 'createdTime' },
]

const SESSION_STATUS_OPTIONS = [
  { value: 'not-started', label: 'Not started' },
  { value: 'in-progress', label: 'In progress' },
  { value: 'finished', label: 'Finished' },
  { value: 'marked-completed', label: 'Marked completed' },
]

// Roles that can call /cc/support/events/actions/* (mirrors the backend controller gate).
const SUPPORT_BOOKING_ACTION_API_ROLES = [
  'ROLE_THERAPY_SUP_L2',
  'ROLE_THERAPY_SUP_L3',
  'ROLE_CLINIC_SUCCESS_MANAGER',
  'ROLE_CLINIC_SUCCESS_ADMIN',
  'ROLE_ADMIN',
]

const FILTER_CONTROL_SX = { minWidth: 190, '& .MuiInputBase-root': { backgroundColor: '#ffffff' } }

/**
 * Support-scope filters (practitioner / clinic / meeting mode / session status) that the
 * generic in-listing filter panel doesn't cover. Values feed the listing via defaultFilters
 * and map 1:1 onto SupportBookedEventInfoSearch params.
 */
function SupportBookingsFilterBar({ value, onChange, showPractitionerFilter }) {
  const [brands, setBrands] = useState(null) // null = loading/unavailable, [] = none
  const [practitionerOptions, setPractitionerOptions] = useState([])
  const [practitionerInput, setPractitionerInput] = useState('')
  const [practitionerSearching, setPractitionerSearching] = useState(false)
  const searchTimerRef = useRef(null)

  useEffect(() => {
    let cancelled = false
    async function loadBrands() {
      const brandList = await fetchClinicBrandsWithLocations()
      if (!cancelled) {
        setBrands(Array.isArray(brandList) ? brandList : [])
      }
    }
    void loadBrands()
    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    if (!showPractitionerFilter) {
      return undefined
    }
    if (searchTimerRef.current) {
      clearTimeout(searchTimerRef.current)
    }
    searchTimerRef.current = setTimeout(async () => {
      setPractitionerSearching(true)
      const practitioners = await searchSupportReassignPractitioners(practitionerInput?.trim() || '')
      setPractitionerOptions(practitioners)
      setPractitionerSearching(false)
    }, 400)
    return () => {
      if (searchTimerRef.current) {
        clearTimeout(searchTimerRef.current)
      }
    }
  }, [practitionerInput, showPractitionerFilter])

  const selectedBrand = useMemo(
    () => (brands || []).find((brand) => brand.id === value.clinicBrandId) || null,
    [brands, value.clinicBrandId],
  )

  const activeFilterCount = ['mentorId', 'clinicBrandId', 'clinicLocationId', 'meetingSrc'].filter(
    (key) => Boolean(value[key]),
  ).length + (value.sessionStatusIds?.length ? 1 : 0)

  const setFilterValue = (patch) => {
    onChange({ ...value, ...patch })
  }

  return (
    <Stack
      direction="row"
      spacing={1.25}
      alignItems="center"
      sx={{ flexWrap: 'wrap', rowGap: 1.25 }}
    >
      {showPractitionerFilter ? (
        <Autocomplete
          size="small"
          sx={{ ...FILTER_CONTROL_SX, minWidth: 230 }}
          options={practitionerOptions}
          value={value.mentorOption || null}
          onChange={(event, nextValue) =>
            setFilterValue({ mentorId: nextValue?.id || undefined, mentorOption: nextValue || undefined })
          }
          inputValue={practitionerInput}
          onInputChange={(event, nextValue) => setPractitionerInput(nextValue)}
          getOptionLabel={(option) => option?.name || option?.slug || ''}
          isOptionEqualToValue={(option, optionValue) => option?.id === optionValue?.id}
          filterOptions={(items) => items}
          loading={practitionerSearching}
          noOptionsText={practitionerInput ? 'No matches' : 'Type to search'}
          renderInput={(params) => (
            <TextField {...params} label="Practitioner" placeholder="Search by name or email" />
          )}
        />
      ) : null}

      {brands && brands.length > 0 ? (
        <TextField
          select
          size="small"
          label="Clinic brand"
          sx={FILTER_CONTROL_SX}
          value={value.clinicBrandId || ''}
          onChange={(event) =>
            setFilterValue({
              clinicBrandId: event.target.value || undefined,
              clinicLocationId: undefined,
            })
          }
        >
          <MenuItem value="">All brands</MenuItem>
          {brands.map((brand) => (
            <MenuItem key={brand.id} value={brand.id}>
              {brand.title}
            </MenuItem>
          ))}
        </TextField>
      ) : null}

      {selectedBrand && selectedBrand.locations?.length > 0 ? (
        <TextField
          select
          size="small"
          label="Location"
          sx={FILTER_CONTROL_SX}
          value={value.clinicLocationId || ''}
          onChange={(event) => setFilterValue({ clinicLocationId: event.target.value || undefined })}
        >
          <MenuItem value="">All locations</MenuItem>
          {selectedBrand.locations.map((location) => (
            <MenuItem key={location.id} value={location.id}>
              {location.title || location.name}
            </MenuItem>
          ))}
        </TextField>
      ) : null}

      <ToggleButtonGroup
        size="small"
        exclusive
        value={value.meetingSrc || 'all'}
        onChange={(event, nextValue) => {
          if (nextValue !== null) {
            setFilterValue({ meetingSrc: nextValue === 'all' ? undefined : nextValue })
          }
        }}
        sx={{ backgroundColor: '#ffffff' }}
      >
        <ToggleButton value="all" sx={{ px: 1.5, textTransform: 'none' }}>
          All
        </ToggleButton>
        <ToggleButton value="aumhum" sx={{ px: 1.5, textTransform: 'none' }}>
          Online
        </ToggleButton>
        <ToggleButton value="offline" sx={{ px: 1.5, textTransform: 'none' }}>
          In-person
        </ToggleButton>
      </ToggleButtonGroup>

      <TextField
        select
        size="small"
        label="Session status"
        sx={FILTER_CONTROL_SX}
        SelectProps={{
          multiple: true,
          renderValue: (selected) =>
            selected.length === 0
              ? 'Any'
              : SESSION_STATUS_OPTIONS.filter((option) => selected.includes(option.value))
                  .map((option) => option.label)
                  .join(', '),
        }}
        value={value.sessionStatusIds || []}
        onChange={(event) =>
          setFilterValue({
            sessionStatusIds:
              event.target.value && event.target.value.length > 0 ? event.target.value : undefined,
          })
        }
      >
        {SESSION_STATUS_OPTIONS.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
      </TextField>

      {activeFilterCount > 0 ? (
        <Button
          size="small"
          color="inherit"
          startIcon={<FilterAltOffOutlinedIcon sx={{ fontSize: 16 }} />}
          onClick={() => onChange({})}
          sx={{ textTransform: 'none', color: '#64748b' }}
        >
          Clear ({activeFilterCount})
        </Button>
      ) : null}
    </Stack>
  )
}

function mergeEventTypeOptions(baseOptions = [], extraOptions = []) {
  const merged = []
  ;[...baseOptions, ...extraOptions].forEach((option) => {
    if (!option?.id || merged.some((existing) => existing.id === option.id)) {
      return
    }
    merged.push(option)
  })
  return merged
}

function getRecordingPolicySourceLabel(source) {
  switch (source) {
    case 'event':
      return 'Event override'
    case 'patient':
      return 'Patient default'
    case 'default-off':
      return 'Default off'
    default:
      return null
  }
}

function isBookedEventCompleted(bookedEvent) {
  return Boolean(
    bookedEvent?.markedCompleted ||
      bookedEvent?.sessionStatus === 'marked-completed' ||
      bookedEvent?.sessionStatus === 'finished',
  )
}

function getSessionMediaCapabilities(bookedEvent, hasVcAuditAccess, canManageRecordingPreferenceForRole = false) {
  const completed = isBookedEventCompleted(bookedEvent)
  const canManageRecordingPreference =
    !completed && canManageRecordingPreferenceForRole && bookedEvent?.canManageRecordingPreference === true
  const isRecordingEnabled = bookedEvent?.resolvedRecordingEnabled === true
  const sourceLabel = getRecordingPolicySourceLabel(bookedEvent?.resolvedRecordingPolicySource)
  const hasMediaAccess = hasVcAuditAccess || canManageRecordingPreference
  const canPlayLive = hasVcAuditAccess && bookedEvent?.vcAuditVisible && bookedEvent?.vcAuditCanViewLive === true
  const recordingCount = Number(bookedEvent?.vcAuditRecordingCount || 0)
  const canPlayRecording =
    hasVcAuditAccess &&
    bookedEvent?.vcAuditVisible &&
    bookedEvent?.vcAuditCanViewRecordings === true &&
    recordingCount > 0
  const latestRecordingId = bookedEvent?.vcAuditLatestRecordingId

  return {
    canManageRecordingPreference,
    canPlayLive,
    canPlayRecording,
    hasAnyMediaAction:
      hasMediaAccess &&
      (canManageRecordingPreference || canPlayLive || canPlayRecording),
    isRecordingEnabled,
    latestRecordingId,
    recordingCount,
    sourceLabel,
  }
}

function SessionMediaActionItems({
  bookedEvent,
  hasVcAuditAccess,
  canManageRecordingPreferenceForRole = false,
  onSuccess,
  BookingActionButton,
}) {
  const navigate = useNavigate()
  const [isUpdating, setIsUpdating] = useState(false)
  const {
    canManageRecordingPreference,
    canPlayLive,
    canPlayRecording,
    hasAnyMediaAction,
    isRecordingEnabled,
    latestRecordingId,
    recordingCount,
    sourceLabel,
  } = getSessionMediaCapabilities(bookedEvent, hasVcAuditAccess, canManageRecordingPreferenceForRole)

  if (!BookingActionButton || !hasAnyMediaAction) {
    return null
  }

  const handleToggle = async () => {
    if (!bookedEvent?.id || isUpdating || !canManageRecordingPreference) {
      return
    }

    setIsUpdating(true)
    const loadingToast = toast.loading('Updating recording preference...')
    const apiUrl = isRecordingEnabled
      ? '/api/secure/cc/support/events/mark-event-as-not-recordable'
      : '/api/secure/cc/support/events/mark-event-as-recordable'

    try {
      const response = await putSPDataWithParams(apiUrl, null, {
        'booked-event-id': bookedEvent.id,
      })

      if (response?.status === 200 && isCommonResponseSuccessfulV2(response)) {
        toast.update(loadingToast, {
          render: isRecordingEnabled
            ? 'Recording disabled for this event'
            : 'Recording enabled for this event',
          type: 'success',
          isLoading: false,
          autoClose: 2500,
        })
        onSuccess?.()
        return
      }

      toast.update(loadingToast, {
        render: response?.data?.message || 'Could not update recording preference',
        type: 'error',
        isLoading: false,
        autoClose: 3500,
      })
    } catch (error) {
      toast.update(loadingToast, {
        render: 'Could not update recording preference',
        type: 'error',
        isLoading: false,
        autoClose: 3500,
      })
    } finally {
      setIsUpdating(false)
    }
  }

  return (
    <>
      {canManageRecordingPreference ? (
        <BookingActionButton
          label={isUpdating ? 'Updating...' : isRecordingEnabled ? 'Recording On' : 'Recording Off'}
          onClick={handleToggle}
          disabled={isUpdating}
          tone="default"
          startIcon={<FiberManualRecordIcon sx={{ fontSize: 15 }} />}
          title={
            sourceLabel
              ? `Recording policy source: ${sourceLabel}.`
              : 'Change the recording preference for this booked event.'
          }
          style={{
            color: isRecordingEnabled ? '#047857' : '#b91c1c',
            borderColor: isRecordingEnabled ? 'rgba(5, 150, 105, 0.34)' : 'rgba(220, 38, 38, 0.32)',
            backgroundColor: isRecordingEnabled ? 'rgba(209, 250, 229, 0.56)' : 'rgba(254, 226, 226, 0.62)',
          }}
        />
      ) : null}
      {canPlayLive ? (
        <BookingActionButton
          label={bookedEvent?.vcAuditLiveBadgeLabel || 'Live'}
          onClick={() =>
            navigate(`/support-home/booked-events/${bookedEvent?.id}/live`, {
              state: { from: '/support-home/booked-events' },
            })
          }
          tone="default"
          startIcon={<PlayCircleOutlineIcon sx={{ fontSize: 17 }} />}
          title="Open live session media."
          style={{
            color: '#b91c1c',
            borderColor: 'rgba(220, 38, 38, 0.34)',
            backgroundColor: 'rgba(254, 226, 226, 0.62)',
          }}
        />
      ) : null}
      {canPlayRecording ? (
        <BookingActionButton
          label={recordingCount > 1 ? `Recordings (${recordingCount})` : 'Recording'}
          onClick={() => {
            const path = latestRecordingId
              ? `/support-home/booked-events/${bookedEvent?.id}/recordings/${latestRecordingId}`
              : `/support-home/booked-events/${bookedEvent?.id}/recordings`
            navigate(path, {
              state: { from: '/support-home/booked-events' },
            })
          }}
          tone="default"
          startIcon={<VideoLibraryOutlinedIcon sx={{ fontSize: 17 }} />}
          title={`Open ${recordingCount} available recording${recordingCount > 1 ? 's' : ''}.`}
        />
      ) : null}
    </>
  )
}

function LegacyBookedEventsView({
  showHeader = true,
  eventTypeOptions = [],
  hasVcAuditAccess = false,
  hasRecordingPreferenceAccess = false,
  hasInvoiceAccess = false,
  heading = 'Bookings & Appointments',
  helperText = '',
  defaultShowOnlyPaidEvents = true,
  defaultFilters = {},
  showPaymentPendingToggle = true,
}) {
  const [selectedEventType, setSelectedEventType] = useState(
    eventTypeOptions[0]?.id || null,
  )
  const [sortingHandler, setSortingHandler] = useState(null)
  const [showOnlyPaidEvents, setShowOnlyPaidEvents] = useState(defaultShowOnlyPaidEvents)
  const [engagementDialogOpen, setEngagementDialogOpen] = useState(false)
  const [selectedEventForEngagement, setSelectedEventForEngagement] = useState(null)
  const [rescheduleDrawerBooking, setRescheduleDrawerBooking] = useState(null)
  const [reassignDrawerBooking, setReassignDrawerBooking] = useState(null)
  const [markPaidDialogBooking, setMarkPaidDialogBooking] = useState(null)
  const [detailDrawerBooking, setDetailDrawerBooking] = useState(null)
  const [supportFilters, setSupportFilters] = useState({})
  const [invoiceDialogState, setInvoiceDialogState] = useState({
    open: false,
    bookedEvent: null,
    openSendDialogOnReady: false,
  })
  const hasEngagementPermission = useMemo(() => canManageEngagement(), [])
  const userRoles = useMemo(() => getUserRoles() || [], [])
  const canUseBookingActionsApi = useMemo(
    () => hasAnySupportRole(userRoles, SUPPORT_BOOKING_ACTION_API_ROLES),
    [userRoles],
  )
  const listingRef = useRef(null)

  const refreshListing = useCallback(() => {
    listingRef.current?.resetData?.()
  }, [])

  // Support-scope filters merged over the page-mode defaults; the listing forwards the extra
  // keys (mentorId / clinicBrandId / clinicLocationId / meetingSrc / sessionStatusIds) to the
  // paginated-unified API. mentorOption is picker-only UI state and stripped here.
  const listingDefaultFilters = useMemo(() => {
    const merged = { ...defaultFilters }
    ;['mentorId', 'clinicBrandId', 'clinicLocationId', 'meetingSrc', 'sessionStatusIds'].forEach(
      (key) => {
        if (supportFilters[key] !== undefined && supportFilters[key] !== null) {
          merged[key] = supportFilters[key]
        }
      },
    )
    return merged
  }, [defaultFilters, supportFilters])

  useEffect(() => {
    if (
      eventTypeOptions.length > 0 &&
      !eventTypeOptions.some((option) => option.id === selectedEventType)
    ) {
      setSelectedEventType(eventTypeOptions[0].id)
    } else if (eventTypeOptions.length === 0 && selectedEventType !== null) {
      setSelectedEventType(null)
    }
  }, [eventTypeOptions, selectedEventType])

  const handleEngagementClick = (bookedEvent) => {
    setSelectedEventForEngagement(bookedEvent)
    setEngagementDialogOpen(true)
  }

  const closeInvoiceDialog = useCallback(() => {
    setInvoiceDialogState({
      open: false,
      bookedEvent: null,
      openSendDialogOnReady: false,
    })
  }, [])

  const openInvoiceDialog = useCallback((bookedEvent, { openSendDialogOnReady = false } = {}) => {
    if (!bookedEvent?.id) {
      return
    }

    setInvoiceDialogState({
      open: true,
      bookedEvent,
      openSendDialogOnReady,
    })
  }, [])

  const receiptCurrentClinic = useMemo(() => {
    const eventData = invoiceDialogState.bookedEvent
    if (!eventData) return null
    const title =
      eventData?.clinicLocation?.title ||
      eventData?.clinicLocation?.name ||
      eventData?.clinicName ||
      eventData?.clinicLocationName ||
      eventData?.clinicLocationTitle
    const address =
      eventData?.clinicLocation?.address ||
      eventData?.address ||
      eventData?.clinicAddress ||
      eventData?.clinicLocationAddress
    const mobile =
      eventData?.clinicLocation?.mobile ||
      eventData?.mobile
    const clinicBrandName =
      eventData?.clinicBrandName ||
      eventData?.clinicBrandData?.brandName ||
      eventData?.clinicBrandData?.name ||
      eventData?.clinicBrandData?.title
    const clinicBrandShortName = eventData?.clinicBrandData?.shortName
    const brandName =
      eventData?.clinicBrandData?.brandName ||
      eventData?.clinicBrandData?.name ||
      eventData?.clinicBrandData?.title

    return {
      clinicBrandName,
      clinicBrandShortName,
      brandName,
      title,
      clinicLocationName: title,
      location: address != null ? { address } : null,
      address,
      mobile,
    }
  }, [invoiceDialogState.bookedEvent])

  return (
    <>
      {showHeader ? <SupportHeader heading={heading} /> : null}
      {helperText ? (
        <Box sx={{ maxWidth: '1200px', margin: '0 auto', width: '100%', px: 1.5, pt: 1.5 }}>
          <Alert severity="info">{helperText}</Alert>
        </Box>
      ) : null}
      <Box sx={{ maxWidth: '1360px', margin: '0 auto', width: '100%', px: 1.5, boxSizing: 'border-box' }}>
        <Paper
          elevation={0}
          sx={{
            mt: 1.5,
            mb: 1.5,
            px: { xs: 1.5, sm: 2 },
            py: 1.25,
            borderRadius: 3,
            border: '1px solid rgba(148, 163, 184, 0.26)',
            backgroundColor: 'rgba(248, 250, 252, 0.9)',
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            spacing={1.5}
            sx={{ flexWrap: 'wrap', rowGap: 1 }}
          >
            <Box sx={{ minWidth: 0 }}>
              {eventTypeOptions.length > 0 ? (
                <BaseUITabsComponent
                  value={selectedEventType}
                  onChange={setSelectedEventType}
                  tabs={eventTypeOptions}
                  getTabLabel={(tab) => tab?.title}
                  getTabId={(tab) => tab?.id}
                  unselectedColor="#64748b"
                  selectedColor="#0f172a"
                  hoverItemColor="#0f172a"
                />
              ) : null}
            </Box>
            <Stack direction="row" alignItems="center" spacing={1.5} sx={{ flexWrap: 'wrap', rowGap: 1 }}>
              {showPaymentPendingToggle ? (
                <FormControlLabel
                  control={
                    <Switch
                      size="small"
                      checked={showOnlyPaidEvents}
                      onChange={() => setShowOnlyPaidEvents(!showOnlyPaidEvents)}
                    />
                  }
                  label={
                    <Typography sx={{ fontSize: 13, color: '#475569', fontWeight: 600 }}>
                      Hide payment pending
                    </Typography>
                  }
                  sx={{ mr: 0 }}
                />
              ) : null}
              <SortingHandler
                sortValue={sortingHandler}
                setSortValue={setSortingHandler}
                getOptionLabel={(option) => option?.label}
                getOptionValue={(option) => option?.value}
                sortOptions={SORT_OPTIONS}
                outerDivStyle={{
                  color: '#475569',
                  fontSize: '14px',
                  alignItems: 'center',
                }}
                buttonColor="primary"
                buttonVariant="outlined"
              />
            </Stack>
          </Stack>
          <Box sx={{ mt: 1.25, pt: 1.25, borderTop: '1px dashed rgba(148, 163, 184, 0.35)' }}>
            <SupportBookingsFilterBar
              value={supportFilters}
              onChange={setSupportFilters}
              showPractitionerFilter={canUseBookingActionsApi}
            />
          </Box>
        </Paper>
      </Box>
      <div style={{ maxWidth: '1360px', margin: '0 auto', width: '100%', padding: '0 12px', boxSizing: 'border-box' }}>
        <MentorPastEventsListing
          ref={listingRef}
          key={`${selectedEventType || 'all'}-${showOnlyPaidEvents}`}
          listingApiSignature="/api/secure/cc/support/events/all/paginated-unified"
          showMentorName
          hidePortalVoiceCallAction
          cancelAppointmentApiSignature="/api/secure/cc/support/events/actions/cancel"
          skipCancelCheckInGuard
          listOfPaymentLinksApiSignature="/api/secure/cc/support/events/user-payment-link/get"
          eventType={selectedEventType || undefined}
          sortingHandler={sortingHandler}
          showOnlyPaidEvents={showOnlyPaidEvents}
          defaultFilters={listingDefaultFilters}
          skipCommunicationTargetsPrefetch
          logsApiEndpoint="/api/secure/cc/support/events/logs/get"
          filterByBookedStatus={null}
          eventTypeOptions={eventTypeOptions}
          onRescheduleBooking={(bookedEvent) => setRescheduleDrawerBooking(bookedEvent)}
          onEngagementClick={hasEngagementPermission ? handleEngagementClick : undefined}
          bookingCardLayout="support"
          renderActionsHeaderTrailing={(bookedEvent) => (
            <SupportContactActions
              patientId={bookedEvent?.patientId}
              practitionerId={bookedEvent?.mentorId}
              bookedEventId={bookedEvent?.id}
              clinicBrandId={bookedEvent?.clinicBrandId}
              clinicId={bookedEvent?.clinicId}
              targetRole="patient"
              eventType={bookedEvent?.eventType || selectedEventType}
              title=""
              compactIconStrip
              uiSurface="support-booked-events-strip"
              sx={{
                px: 0.45,
                py: 0.3,
                borderRadius: 999,
                border: '1px solid rgba(96, 165, 250, 0.22)',
                background:
                  'linear-gradient(135deg, rgba(239, 246, 255, 0.94) 0%, rgba(240, 253, 250, 0.9) 100%)',
              }}
            />
          )}
          renderManageSessionActionItems={(bookedEvent, { BookingActionButton }) => {
            const sessionMediaCapabilities = getSessionMediaCapabilities(
              bookedEvent,
              hasVcAuditAccess,
              hasRecordingPreferenceAccess,
            )
            const canManageThisBooking =
              canUseBookingActionsApi && canActOnSupportBookedEvent(userRoles, bookedEvent?.eventType)

            const items = []
            if (sessionMediaCapabilities.hasAnyMediaAction) {
              items.push(
                <SessionMediaActionItems
                  key="session-media-actions"
                  bookedEvent={bookedEvent}
                  hasVcAuditAccess={hasVcAuditAccess}
                  canManageRecordingPreferenceForRole={hasRecordingPreferenceAccess}
                  onSuccess={refreshListing}
                  BookingActionButton={BookingActionButton}
                />,
              )
            }
            if (canManageThisBooking && bookedEvent?.id) {
              items.push(
                <SupportBookedEventManageMenu
                  key="support-manage-menu"
                  bookedEvent={bookedEvent}
                  BookingActionButton={BookingActionButton}
                  canMarkPaid={canMarkSupportBookedEventPaid(userRoles, bookedEvent?.eventType)}
                  onReassign={(eventForReassign) => setReassignDrawerBooking(eventForReassign)}
                  onMarkPaid={(eventForPayment) => setMarkPaidDialogBooking(eventForPayment)}
                  onOpenDetails={(eventForDetails) => setDetailDrawerBooking(eventForDetails)}
                  onActionSuccess={refreshListing}
                />,
              )
            }
            return items.length > 0 ? items : null
          }}
          renderViewActionItems={(bookedEvent, { BookingActionButton }) => {
            if (!bookedEvent?.id) {
              return null
            }

            const items = [
              <BookingActionButton
                key="support-details"
                label="Details"
                onClick={() => setDetailDrawerBooking(bookedEvent)}
                tone="view"
                startIcon={<InfoOutlinedIcon sx={{ fontSize: 17 }} />}
                title="Full booking details: schedule, payment, links, history and support activity"
              />,
            ]

            if (hasInvoiceAccess) {
              items.push(
                <BookingActionButton
                  key="generate-invoice"
                  label="Generate Invoice"
                  onClick={() => openInvoiceDialog(bookedEvent)}
                  tone="payment"
                  startIcon={<ReceiptLongOutlinedIcon sx={{ fontSize: 17 }} />}
                />,
                <BookingActionButton
                  key="send-invoice"
                  label="Send Invoice"
                  onClick={() => openInvoiceDialog(bookedEvent, { openSendDialogOnReady: true })}
                  tone="payment"
                  startIcon={<SendOutlinedIcon sx={{ fontSize: 17 }} />}
                />,
              )
            }
            return items
          }}
        />
      </div>

      {hasEngagementPermission && (
        <EngagementSettingsDialog
          open={engagementDialogOpen}
          onClose={() => {
            setEngagementDialogOpen(false)
            setSelectedEventForEngagement(null)
          }}
          targetType="booked-event"
          target={selectedEventForEngagement}
          onSuccess={() => {}}
        />
      )}

      <SupportBookedEventRescheduleDrawer
        open={Boolean(rescheduleDrawerBooking)}
        bookedEvent={rescheduleDrawerBooking}
        onClose={() => setRescheduleDrawerBooking(null)}
        onSuccess={() => {
          setRescheduleDrawerBooking(null)
          refreshListing()
        }}
      />

      <SupportBookedEventReassignDrawer
        open={Boolean(reassignDrawerBooking)}
        bookedEvent={reassignDrawerBooking}
        onClose={() => setReassignDrawerBooking(null)}
        onSuccess={() => {
          setReassignDrawerBooking(null)
          refreshListing()
        }}
      />

      <SupportMarkAsPaidDialog
        open={Boolean(markPaidDialogBooking)}
        bookedEvent={markPaidDialogBooking}
        onClose={() => setMarkPaidDialogBooking(null)}
        onSuccess={() => {
          setMarkPaidDialogBooking(null)
          refreshListing()
        }}
      />

      <SupportBookedEventDetailDrawer
        open={Boolean(detailDrawerBooking)}
        bookedEvent={detailDrawerBooking}
        onClose={() => setDetailDrawerBooking(null)}
      />

      <Dialog
        open={invoiceDialogState.open}
        PaperComponent={ReceiptPrintDialogPaper}
        onClose={closeInvoiceDialog}
        className="receipt-print-dialog"
      >
        <Suspense
          fallback={
            <Box sx={{ p: 3, display: 'flex', justifyContent: 'center' }}>
              <CircularProgress />
            </Box>
          }
        >
          <ReceiptPrintingComponent
            bookedEvent={invoiceDialogState.bookedEvent}
            bookedEventId={invoiceDialogState.bookedEvent?.id}
            patientSearchApiSignature="/api/secure/cc/support/patient/search"
            closeDialog={closeInvoiceDialog}
            fetchDataWithoutParams={getSPDataWithoutParams}
            currentClinic={receiptCurrentClinic}
            sendReceiptApiSignature="/api/secure/cc/support/receipt/send"
            postDataForSendReceipt={postSPDataWithoutParams}
            documentKindLabel="Invoice"
            openSendDialogOnReady={invoiceDialogState.openSendDialogOnReady}
          />
        </Suspense>
      </Dialog>
    </>
  )
}

export default function BookedEvents({ pageMode = 'default' }) {
  const userRoles = getUserRoles()
  const hasVcAuditAccess = canViewSupportVcAuditBookings(userRoles)
  const hasSupportBookingsAccess = canViewSupportBookings(userRoles)
  const hasRecordingPreferenceAccess = canManageSupportRecordingPreferences(userRoles)
  const hasInvoiceAccess = canManageSupportBookedEventInvoices(userRoles)
  const allowedSupportEventTypes = useMemo(() => {
    const baseOptions = getAllowedSupportBookedEventTypeOptions(userRoles)
    if (!hasVcAuditAccess) {
      return baseOptions
    }
    return mergeEventTypeOptions(baseOptions, SUPPORT_BOOKED_EVENT_TYPE_OPTIONS_CLINIC_SUCCESS)
  }, [hasVcAuditAccess, userRoles])
  const pageConfig = useMemo(() => {
    if (pageMode === 'payment-links') {
      return {
        heading: 'Payment Links',
        helperText: 'Review payment-pending bookings and open their payment links from one place.',
        defaultShowOnlyPaidEvents: false,
        defaultFilters: {
          paymentStatusIds: [BOOKING_PAYMENT_STATUSES.PENDING],
        },
        showPaymentPendingToggle: false,
      }
    }

    return {
      heading: 'Bookings & Appointments',
      helperText: '',
      defaultShowOnlyPaidEvents: true,
      defaultFilters: {},
      showPaymentPendingToggle: true,
    }
  }, [pageMode])

  if (!hasVcAuditAccess && !hasSupportBookingsAccess) {
    return <Navigate to="/support-home" replace />
  }

  return (
    <LegacyBookedEventsView
      showHeader
      eventTypeOptions={allowedSupportEventTypes}
      hasVcAuditAccess={hasVcAuditAccess}
      hasRecordingPreferenceAccess={hasRecordingPreferenceAccess}
      hasInvoiceAccess={hasInvoiceAccess}
      heading={pageConfig.heading}
      helperText={pageConfig.helperText}
      defaultShowOnlyPaidEvents={pageConfig.defaultShowOnlyPaidEvents}
      defaultFilters={pageConfig.defaultFilters}
      showPaymentPendingToggle={pageConfig.showPaymentPendingToggle}
    />
  )
}
